import {DatePipe} from "@angular/common";
import {Component, input, output} from "@angular/core";
import {RouterLink} from "@angular/router";
import type {NewsAuthor} from "../../../models/News/NewsAuthor";
import type {NewsEntry} from "../../../models/News/NewsEntry";
import type {NewsFilter} from "../../../models/NewsFilter";
import type {Tag} from "../../../models/Tag";
import {SafeHtmlPipe} from "../../../pipes/safe-html.pipe";

@Component({
  selector: "app-news-shear",
  templateUrl: "./news-shear.component.html",
  styleUrl: "./news-shear.component.scss",
  imports: [
    RouterLink,
    DatePipe,
    SafeHtmlPipe,
  ],
})
export class NewsShearComponent {
  public readonly newsEntry = input.required<NewsEntry>();
  public readonly isDetail = input.required<boolean>();

  public readonly filterChange = output<NewsFilter>();

  protected prepareForQueryParameter(title: string): string {
    return title.replace(/\s/g, "-").toLowerCase();
  }

  protected getContentHtml(html: string, truncate: boolean): string {
    return truncate ? this.truncateNewsContent(html) : html;
  }

  private truncateNewsContent(html: string): string {
    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    const limit = html.substring(0, 500).lastIndexOf(" ");
    let truncated = html.substring(0, limit);
    // If the text ends with an incomplete start tag, trim it off
    truncated = truncated.replace(/<(\w*)(?:\s\w+=?(["']?)\w*\2?)*$/g, "");
    // If the text ends with a truncated end tag, fix it.
    const truncatedEndTagExpr = /<\/(\w*)$/g;
    const truncatedEndTagMatch = truncatedEndTagExpr.exec(truncated);
    if (truncatedEndTagMatch != null) {
      const truncatedEndTag = truncatedEndTagMatch[1];
      // Check to see if there's an identifiable tag in the end tag
      if (truncatedEndTag.length > 0) {
        // If so, find the start tag and close it
        const startTagExpr = new RegExp("<(" + truncatedEndTag + "\\w?)(?:(?:\\s\\w+(?:=([\"'])\\w*\\2)))*>");
        let testString = truncated;
        let startTagMatch = startTagExpr.exec(testString);

        let startTag = null;
        while (startTagMatch != null) {
          startTag = startTagMatch[1];
          testString = testString.replace(startTagExpr, "");
          startTagMatch = startTagExpr.exec(testString);
        }
        if (startTag != null)
          truncated = truncated.replace(truncatedEndTagExpr, `</${startTag}>`);
      } else {
        // Otherwise, cull off the broken end tag
        truncated = truncated.replace(truncatedEndTagExpr, "");
      }
    }

    // Now the tricky part. Reverse the text and look for opening tags.
    // For each opening tag, check to see that the closing tag before it is for that tag. If not, append a closing tag.
    let testString = this.reverseHtml(truncated);
    const reverseTagOpenExpr = /<(?:(["'])\w*\1=\w+ )*(\w*)>/;
    let tagMatch = reverseTagOpenExpr.exec(testString);
    while (tagMatch != null) {
      // eslint-disable-next-line @typescript-eslint/no-magic-numbers
      const tagName = tagMatch[2];
      const startPos = tagMatch.index;
      const fragment = testString.substring(startPos);

      truncated += `</${this.reverseHtml(tagName)}>`;
      // Get rid of the already tested fragment
      testString = testString.replace(fragment, "");
      // Get another tag to test
      tagMatch = reverseTagOpenExpr.exec(testString);
    }
    return truncated;
  }

  private reverseHtml(str: string): string {
    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    const ph = String.fromCharCode(206);
    let result = str.split("").reverse().join("");
    while (result.includes("<")) result = result.replace("<", ph);
    while (result.includes(">")) result = result.replace(">", "<");
    while (result.includes(ph)) result = result.replace(ph, ">");
    return result;
  }

  protected filterByAuthor(author: Readonly<NewsAuthor>): void {
    this.filterChange.emit({key: "author", value: {raw: author.username, display: author.fullName}});
  }

  protected filterByTag(tag: Readonly<Tag>): void {
    this.filterChange.emit({key: "tag", value: {raw: tag.slug, display: tag.name}});
  }
}
